import { useState, useEffect } from 'react';
import { Plus, Home, CreditCard, Settings, Wallet, TrendingUp, TrendingDown, PieChart as PieChartIcon,
  Search, Filter, Download, Upload, Edit2, Trash2, ChevronDown, RefreshCw, Menu, X, ChevronRight, MoreVertical,
  Eye, EyeOff, Target, Sparkles, Shield, Cloud, Smartphone, Tablet, Monitor, Lock, Calendar, } from 'lucide-react';
import { useTransactions } from './hooks/useTransactions';
import IncomeExpenseChart from './components/charts/IncomeExpenseChart';
import SpendingByCategoryChart from './components/charts/SpendingByCategoryChart';
import MonthlyTrendChart from './components/charts/MonthlyTrendChart';
import TransactionForm from './components/transactions/TransactionForm';
import ImportModal from './components/ImportModal';
import { Transaction } from './types';
import { loadSampleData, clearAllData } from './utils/sampleData';
import { convertTransactionsToCSV } from './utils/csvUtils';

function App() {
  const [showForm, setShowForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'transactions' | 'settings'>('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showBalance, setShowBalance] = useState(true);
  
  const {
    transactions,
    stats,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    importTransactions,
  } = useTransactions();

  // Filter transactions based on search
  const filteredTransactions = transactions.filter(transaction =>
    transaction.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    transaction.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    transaction.paymentMethod?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddTransaction = (transactionData: Omit<Transaction, 'id'>) => {
    addTransaction(transactionData);
    setShowForm(false);
  };

  const handleUpdateTransaction = (id: string, updates: Partial<Transaction>) => {
    updateTransaction(id, updates);
    setEditingTransaction(null);
  };

  const handleDeleteTransaction = (id: string) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      deleteTransaction(id);
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      window.location.reload();
      setIsRefreshing(false);
    }, 300);
  };

  const handleImportTransactions = (transactionsData: Omit<Transaction, 'id'>[]) => {
    importTransactions(transactionsData);
    setShowImportModal(false);
  };

  const exportToCSV = (transactionsToExport: Transaction[]) => {
    const csvContent = convertTransactionsToCSV(transactionsToExport);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Auto-refresh data every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Get recent transactions
  const recentTransactions = filteredTransactions.slice(0, 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-950 text-gray-100">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-gray-900 to-gray-950 
        border-r border-gray-800/50 backdrop-blur-xl z-50 
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 lg:block lg:z-30
      `}>
        <div className="p-6 h-full flex flex-col">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Stanza</h1>
                <p className="text-xs text-gray-400"> by Delong</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-gray-800/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          <nav className="space-y-1 flex-1">
            <button
              onClick={() => { setActiveTab('dashboard'); setSidebarOpen(false); }}
              className={`nav-item w-full ${activeTab === 'dashboard' ? 'nav-item-active' : 'nav-item-inactive'}`}
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Dashboard</span>
              {activeTab === 'dashboard' && (
                <ChevronRight className="w-4 h-4 ml-auto" />
              )}
            </button>
            
            <button
              onClick={() => { setActiveTab('transactions'); setSidebarOpen(false); }}
              className={`nav-item w-full ${activeTab === 'transactions' ? 'nav-item-active' : 'nav-item-inactive'}`}
            >
              <CreditCard className="w-5 h-5" />
              <span className="font-medium">Transactions</span>
              {activeTab === 'transactions' && (
                <ChevronRight className="w-4 h-4 ml-auto" />
              )}
            </button>
            
            <button
              onClick={() => { setActiveTab('settings'); setSidebarOpen(false); }}
              className={`nav-item w-full ${activeTab === 'settings' ? 'nav-item-active' : 'nav-item-inactive'}`}
            >
              <Settings className="w-5 h-5" />
              <span className="font-medium">Settings</span>
              {activeTab === 'settings' && (
                <ChevronRight className="w-4 h-4 ml-auto" />
              )}
            </button>

            <div className="pt-6 mt-6 border-t border-gray-800/50">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 px-4">Quick Actions</p>
              <button
                onClick={() => { setShowForm(true); setSidebarOpen(false); }}
                className="nav-item w-full nav-item-inactive"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Add Transaction</span>
              </button>
              <button
                onClick={() => { setShowImportModal(true); setSidebarOpen(false); }}
                className="nav-item w-full nav-item-inactive"
              >
                <Upload className="w-5 h-5" />
                <span className="font-medium">Import CSV</span>
              </button>
            </div>
          </nav>

          {/* Quick Stats */}
          <div className="mt-auto pt-6 border-t border-gray-800/50">
            <div className="stats-card p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-400">Total Balance</p>
                <button
                  onClick={() => setShowBalance(!showBalance)}
                  className="p-1 hover:bg-gray-800/50 rounded"
                >
                  {showBalance ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-2xl font-bold text-white">
                {showBalance ? formatCurrency(stats.totalBalance) : '••••••'}
              </p>
              <div className="flex items-center mt-3">
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${Math.min(Math.max(stats.savingsRate, 0), 100)}%`,
                      opacity: stats.savingsRate > 0 ? 1 : 0.3
                    }}
                  />
                </div>
                <span className="text-xs text-gray-400 ml-2 min-w-[60px] text-right">
                  {stats.savingsRate > 0 ? `${stats.savingsRate}% saved` : 'No savings'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        min-h-screen transition-all duration-300
        lg:ml-64
      `}>
        {/* Top Navigation */}
        <header className="sticky top-0 z-20 bg-gradient-to-r from-gray-900/80 to-gray-900/40 backdrop-blur-xl border-b border-gray-800/50">
          <div className="px-4 py-3 lg:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 hover:bg-gray-800/50 rounded-lg transition-colors"
                >
                  <Menu className="w-5 h-5 text-gray-400" />
                </button>
                
                <div className="hidden xs:block">
                  <h2 className="text-lg font-semibold text-white">
                    {activeTab === 'dashboard' && 'Financial Dashboard'}
                    {activeTab === 'transactions' && 'Transaction Management'}
                    {activeTab === 'settings' && 'Settings'}
                  </h2>
                  <p className="text-sm text-gray-400 hidden sm:block">
                    {activeTab === 'dashboard' && 'Track and analyze your finances'}
                    {activeTab === 'transactions' && 'Manage income and expenses'}
                    {activeTab === 'settings' && 'Data management and preferences'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {/* Responsive Search - Hidden on xs, shown on sm */}
                {activeTab === 'transactions' && (
                  <div className="hidden sm:block relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="input-field pl-10 pr-4 w-48 md:w-64"
                    />
                  </div>
                )}
                
                <button
                  onClick={handleRefresh}
                  className={`p-2.5 btn-secondary ${isRefreshing ? 'animate-spin' : ''}`}
                  title="Refresh data"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
                
                <button
                  onClick={() => setShowForm(true)}
                  className="btn-primary p-2.5 lg:px-4 lg:py-2.5 flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span className="hidden lg:inline">Add Transaction</span>
                </button>
              </div>
            </div>

            {/* Mobile Search - Only shown on xs for transactions tab */}
            {activeTab === 'transactions' && (
              <div className="mt-3 sm:hidden">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field w-full pl-10 pr-4"
                  />
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Content Area */}
        <div className="p-4 lg:p-6">
          {/* Dashboard Content */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6 animate-fade-in">
              {/* Stats Overview */}
              <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="stats-card group">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Total Balance</p>
                      <p className="text-2xl font-bold text-white">${stats.totalBalance.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-blue-500/10 rounded-xl group-hover:bg-blue-500/20 transition-colors">
                      <Wallet className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                  <div className="flex items-center mt-4">
                    <TrendingUp className="w-4 h-4 text-green-400 mr-2" />
                    <span className="text-sm text-gray-400">Overall financial health</span>
                  </div>
                </div>
                
                <div className="stats-card group">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Monthly Income</p>
                      <p className="text-2xl font-bold text-green-400">${stats.monthlyIncome.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-green-500/10 rounded-xl group-hover:bg-green-500/20 transition-colors">
                      <TrendingUp className="w-6 h-6 text-green-400" />
                    </div>
                  </div>
                  <div className="flex items-center mt-4">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-400">This month</span>
                  </div>
                </div>
                
                <div className="stats-card group">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Monthly Expenses</p>
                      <p className="text-2xl font-bold text-red-400">${stats.monthlyExpenses.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded-xl group-hover:bg-red-500/20 transition-colors">
                      <TrendingDown className="w-6 h-6 text-red-400" />
                    </div>
                  </div>
                  <div className="flex items-center mt-4">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-400">This month</span>
                  </div>
                </div>
                
                <div className="stats-card group">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Savings Rate</p>
                      <p className="text-2xl font-bold text-purple-400">{stats.savingsRate}%</p>
                    </div>
                    <div className="p-3 bg-purple-500/10 rounded-xl group-hover:bg-purple-500/20 transition-colors">
                      <PieChartIcon className="w-6 h-6 text-purple-400" />
                    </div>
                  </div>
                  <div className="flex items-center mt-4">
                    <Target className="w-4 h-4 text-purple-400 mr-2" />
                    <span className="text-sm text-gray-400">Financial efficiency</span>
                  </div>
                </div>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="chart-container">
                  <IncomeExpenseChart transactions={transactions} />
                </div>
                <div className="chart-container">
                  <SpendingByCategoryChart transactions={transactions} />
                </div>
              </div>
              
              <div className="chart-container">
                <MonthlyTrendChart transactions={transactions} />
              </div>

              {/* Recent Transactions & Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="card lg:col-span-2">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-white">Recent Transactions</h3>
                      <p className="text-sm text-gray-400">Latest activity</p>
                    </div>
                    <button
                      onClick={() => setActiveTab('transactions')}
                      className="btn-secondary flex items-center space-x-2 px-4 py-2"
                    >
                      <span>View All</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    {recentTransactions.map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between p-3 hover:bg-gray-800/30 rounded-xl transition-colors group">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${transaction.type === 'income' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                            {transaction.type === 'income' ? (
                              <TrendingUp className="w-5 h-5 text-green-400" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-white">{transaction.description}</p>
                            <p className="text-sm text-gray-400">{transaction.category} • {formatDate(transaction.date)}</p>
                          </div>
                        </div>
                        <div className={`font-bold ${transaction.type === 'income' ? 'text-green-400' : 'text-red-400'}`}>
                          {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                        </div>
                      </div>
                    ))}
                    {recentTransactions.length === 0 && (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CreditCard className="w-8 h-8 text-gray-600" />
                        </div>
                        <p className="text-gray-400 mb-2">No transactions yet</p>
                        <button
                          onClick={() => setShowForm(true)}
                          className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                        >
                          Add your first transaction
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Insights */}
                <div className="card">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-white">Quick Insights</h3>
                      <p className="text-sm text-gray-400">Financial overview</p>
                    </div>
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-3 bg-gray-800/30 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-400">Avg Monthly Income</span>
                        <span className="text-sm font-medium text-green-400">
                          ${transactions.length > 0 ? Math.round(stats.monthlyIncome) : '0'}
                        </span>
                      </div>
                      <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((stats.monthlyIncome / 10000) * 100, 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="p-3 bg-gray-800/30 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-400">Avg Monthly Expenses</span>
                        <span className="text-sm font-medium text-red-400">
                          ${transactions.length > 0 ? Math.round(stats.monthlyExpenses) : '0'}
                        </span>
                      </div>
                      <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-red-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((stats.monthlyExpenses / 5000) * 100, 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="p-3 bg-gray-800/30 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-400">Transactions Count</span>
                        <span className="text-sm font-medium text-blue-400">
                          {transactions.length}
                        </span>
                      </div>
                      <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((transactions.length / 100) * 100, 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="p-3 bg-gray-800/30 rounded-xl">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">Categories Used</span>
                        <span className="text-sm font-medium text-purple-400">
                          {new Set(transactions.map(t => t.category)).size}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="animate-fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-white">All Transactions</h2>
                  <p className="text-sm text-gray-400">{filteredTransactions.length} transactions found</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button className="btn-secondary flex items-center space-x-2 px-3 py-2">
                    <Filter className="w-4 h-4" />
                    <span>Filter</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="btn-secondary flex items-center space-x-2 px-3 py-2"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="hidden xs:inline">Import CSV</span>
                  </button>
                  <button
                    onClick={() => exportToCSV(filteredTransactions)}
                    className="btn-secondary flex items-center space-x-2 px-3 py-2"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden xs:inline">Export CSV</span>
                  </button>
                </div>
              </div>
              
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr className="table-header">
                      <th className="table-header-cell">Date</th>
                      <th className="table-header-cell">Description</th>
                      <th className="table-header-cell hidden sm:table-cell">Category</th>
                      <th className="table-header-cell hidden md:table-cell">Payment</th>
                      <th className="table-header-cell">Amount</th>
                      <th className="table-header-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((transaction) => (
                      <tr key={transaction.id} className="table-row group">
                        <td className="table-cell">
                          <div className="text-gray-300">{formatDate(transaction.date)}</div>
                        </td>
                        
                        <td className="table-cell">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${transaction.type === 'income' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                              {transaction.type === 'income' ? (
                                <TrendingUp className="w-4 h-4 text-green-400" />
                              ) : (
                                <TrendingDown className="w-4 h-4 text-red-400" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-white">{transaction.description}</p>
                              <p className="text-sm text-gray-400 sm:hidden">{transaction.category}</p>
                            </div>
                          </div>
                        </td>
                        
                        <td className="table-cell hidden sm:table-cell">
                          <span className="badge badge-info">{transaction.category}</span>
                        </td>
                        
                        <td className="table-cell hidden md:table-cell">
                          <span className="text-gray-300">{transaction.paymentMethod}</span>
                        </td>
                        
                        <td className="table-cell">
                          <span className={`font-bold ${transaction.type === 'income' ? 'text-green-400' : 'text-red-400'}`}>
                            {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                          </span>
                        </td>
                        
                        <td className="table-cell">
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleEdit(transaction)}
                              className="p-2 text-blue-400 hover:text-blue-300 transition-colors hover:bg-blue-500/10 rounded-lg"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              className="p-2 text-red-400 hover:text-red-300 transition-colors hover:bg-red-500/10 rounded-lg"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-300 transition-colors hover:bg-gray-500/10 rounded-lg opacity-0 group-hover:opacity-100">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredTransactions.length === 0 && (
                  <div className="py-12 text-center">
                    <div className="w-20 h-20 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="w-10 h-10 text-gray-600" />
                    </div>
                    <p className="text-gray-400 mb-2">No transactions found</p>
                    <p className="text-sm text-gray-500 mb-4">Try adding a transaction or adjusting your search</p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      <button
                        onClick={() => setShowForm(true)}
                        className="btn-primary px-4 py-2"
                      >
                        Add Transaction
                      </button>
                      <button
                        onClick={loadSampleData}
                        className="btn-secondary px-4 py-2"
                      >
                        Load Sample Data
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {filteredTransactions.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-4 text-sm text-gray-400">
                  <div className="mb-2 sm:mb-0">
                    Showing {filteredTransactions.length} of {transactions.length} transactions
                  </div>
                  <div className="flex items-center space-x-4">
                    <button className="hover:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      Previous
                    </button>
                    <span className="text-white">1</span>
                    <button className="hover:text-gray-300 transition-colors">
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6 animate-fade-in">
              {/* Data Management */}
              <div className="card">
                <h3 className="text-lg font-semibold text-white mb-4">Data Management</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="card-hover p-4 text-left"
                  >
                    <div className="p-3 bg-blue-500/10 rounded-xl w-fit mb-3">
                      <Upload className="w-6 h-6 text-blue-400" />
                    </div>
                    <p className="font-medium text-white mb-1">Import from CSV</p>
                    <p className="text-sm text-gray-400">Import transactions from a CSV file</p>
                  </button>
                  
                  <button
                    onClick={() => exportToCSV(transactions)}
                    className="card-hover p-4 text-left"
                  >
                    <div className="p-3 bg-green-500/10 rounded-xl w-fit mb-3">
                      <Download className="w-6 h-6 text-green-400" />
                    </div>
                    <p className="font-medium text-white mb-1">Export to CSV</p>
                    <p className="text-sm text-gray-400">Download your data for backup</p>
                  </button>
                  
                  <button
                    onClick={loadSampleData}
                    className="card-hover p-4 text-left"
                  >
                    <div className="p-3 bg-purple-500/10 rounded-xl w-fit mb-3">
                      <Sparkles className="w-6 h-6 text-purple-400" />
                    </div>
                    <p className="font-medium text-white mb-1">Load Sample Data</p>
                    <p className="text-sm text-gray-400">Add example transactions</p>
                  </button>

                  <button
                    onClick={clearAllData}
                    className="card-hover p-4 text-left border-red-500/20 hover:border-red-500/40"
                  >
                    <div className="p-3 bg-red-500/10 rounded-xl w-fit mb-3">
                      <Trash2 className="w-6 h-6 text-red-400" />
                    </div>
                    <p className="font-medium text-white mb-1">Clear All Data</p>
                    <p className="text-sm text-gray-400">Remove all transactions</p>
                  </button>

                  <div className="card-hover p-4">
                    <div className="p-3 bg-yellow-500/10 rounded-xl w-fit mb-3">
                      <Shield className="w-6 h-6 text-yellow-400" />
                    </div>
                    <p className="font-medium text-white mb-1">Data Security</p>
                    <p className="text-sm text-gray-400">All data stored locally</p>
                  </div>

                  <div className="card-hover p-4">
                    <div className="p-3 bg-cyan-500/10 rounded-xl w-fit mb-3">
                      <Cloud className="w-6 h-6 text-cyan-400" />
                    </div>
                    <p className="font-medium text-white mb-1">Cloud Backup</p>
                    <p className="text-sm text-gray-400">Coming soon</p>
                  </div>
                </div>
              </div>

              {/* App Stats */}
              <div className="card">
                <h3 className="text-lg font-semibold text-white mb-4">App Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-800/30 rounded-xl">
                    <p className="text-2xl font-bold text-white">{transactions.length}</p>
                    <p className="text-sm text-gray-400">Transactions</p>
                  </div>
                  <div className="p-4 bg-gray-800/30 rounded-xl">
                    <p className="text-2xl font-bold text-green-400">${stats.totalBalance.toLocaleString()}</p>
                    <p className="text-sm text-gray-400">Total Balance</p>
                  </div>
                  <div className="p-4 bg-gray-800/30 rounded-xl">
                    <p className="text-2xl font-bold text-blue-400">{new Set(transactions.map(t => t.category)).size}</p>
                    <p className="text-sm text-gray-400">Categories</p>
                  </div>
                  <div className="p-4 bg-gray-800/30 rounded-xl">
                    <p className="text-2xl font-bold text-purple-400">{stats.savingsRate}%</p>
                    <p className="text-sm text-gray-400">Savings Rate</p>
                  </div>
                </div>
              </div>

              {/* Device Compatibility */}
              <div className="card">
                <h3 className="text-lg font-semibold text-white mb-4">Device Compatibility</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-800/30 rounded-xl flex items-center space-x-3">
                    <Smartphone className="w-8 h-8 text-blue-400" />
                    <div>
                      <p className="font-medium text-white">Mobile</p>
                      <p className="text-sm text-gray-400">Fully responsive</p>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-800/30 rounded-xl flex items-center space-x-3">
                    <Tablet className="w-8 h-8 text-green-400" />
                    <div>
                      <p className="font-medium text-white">Tablet</p>
                      <p className="text-sm text-gray-400">Optimized layout</p>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-800/30 rounded-xl flex items-center space-x-3">
                    <Monitor className="w-8 h-8 text-purple-400" />
                    <div>
                      <p className="font-medium text-white">Desktop</p>
                      <p className="text-sm text-gray-400">Enhanced features</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* App Info */}
              <div className="card">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Wallet className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">Stanza</p>
                      <p className="text-gray-400">Version 2.1 • © 2025 Stanza. All Rights Reserved.</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Lock className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-gray-400">Secure • Private • Local</span>
                  </div>
                </div>
                <p className="text-sm text-gray-400 mt-4">
                  A clean, focused personal finance tracker with essential analytics. 
                  All data is stored locally in your browser for maximum privacy and security.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="badge badge-success">No Sign-up Required</span>
                  <span className="badge badge-info">100% Free</span>
                  <span className="badge badge-warning">No Tracking</span>
                  <span className="badge badge-danger">Data Never Leaves Your Browser</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 to-gray-900/95 backdrop-blur-xl border-t border-gray-800/50 z-20">
          <div className="flex items-center justify-around p-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex flex-col items-center p-2 rounded-xl transition-all ${activeTab === 'dashboard' ? 'text-blue-400 bg-blue-500/10' : 'text-gray-400'}`}
            >
              <Home className="w-5 h-5 mb-1" />
              <span className="text-xs">Dashboard</span>
            </button>
            
            <button
              onClick={() => setActiveTab('transactions')}
              className={`flex flex-col items-center p-2 rounded-xl transition-all ${activeTab === 'transactions' ? 'text-blue-400 bg-blue-500/10' : 'text-gray-400'}`}
            >
              <CreditCard className="w-5 h-5 mb-1" />
              <span className="text-xs">Transactions</span>
            </button>
            
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary p-3 rounded-full -mt-4 shadow-xl animate-pulse-glow"
            >
              <Plus className="w-6 h-6" />
            </button>
            
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex flex-col items-center p-2 rounded-xl transition-all ${activeTab === 'settings' ? 'text-blue-400 bg-blue-500/10' : 'text-gray-400'}`}
            >
              <Settings className="w-5 h-5 mb-1" />
              <span className="text-xs">Settings</span>
            </button>
            
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex flex-col items-center p-2 rounded-xl text-gray-400 hover:text-white transition-colors"
            >
              <Menu className="w-5 h-5 mb-1" />
              <span className="text-xs">More</span>
            </button>
          </div>
        </div>

        {/* Add padding for mobile bottom nav */}
        <div className="h-16 lg:h-0" />
      </main>

      {/* Transaction Form Modal */}
      {(showForm || editingTransaction) && (
        <div className="modal-overlay">
          <div className="modal-container">
            <TransactionForm
              onSave={editingTransaction ? 
                (data) => handleUpdateTransaction(editingTransaction.id, data) : 
                handleAddTransaction}
              onCancel={() => {
                setShowForm(false);
                setEditingTransaction(null);
              }}
              transaction={editingTransaction || undefined}
            />
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-container max-w-4xl">
            <ImportModal
              onImport={handleImportTransactions}
              onCancel={() => setShowImportModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;